// Local-first storage. Invariant 1: registration is written here BEFORE any
// network call, and the success screen renders off this write. The network is
// an afterthought that catches up later.
import { openDB, type DBSchema, type IDBPDatabase } from "idb";

export type SyncState = "pending" | "synced" | "failed";

/**
 * "backdrop" is a camera shot; "external" is an image generated outside this
 * system and brought in from the laptop. They upload identically — the kind
 * only picks which provenance the server records.
 */
export type UploadKind = "backdrop" | "external";

export interface UploadRecord {
  id: string; // client UUID v4 — becomes media.id for backdrop shots
  guestId: string;
  guestCode: string;
  kind: UploadKind;
  /**
   * The ORIGINAL capture, written before anything else touches it.
   *
   * Compression is expensive (a 12MP frame takes a few hundred ms), and any
   * work done between "staff picked the files" and "it is on disk" is work that
   * a refresh or a crash silently destroys — the photos are still only an
   * in-memory FileList. So ingest persists the raw blob and stops; the queue
   * compresses later. Nulled the moment `web` exists.
   */
  raw: Blob | null;
  /**
   * Compressed image blobs, held in IndexedDB so a refresh cannot lose them
   * (Invariant 4). NULLED once the upload is confirmed: 500 photos at ~400KB
   * would otherwise fill the staff device's storage quota mid-event.
   */
  web: Blob | null;
  thumb: Blob | null;
  bytes: number;
  createdAt: string;
  sync: SyncState;
  attempts: number;
  lastAttemptAt: string | null;
  lastError: string | null;
}

interface LaqtaDB extends DBSchema {
  /**
   * Empty as of the booth rewrite — nothing reads or writes it.
   *
   * The STORE is kept because deleting an object store is a schema change, and
   * a schema change is another version bump on a database that already holds
   * the upload queue. There is no gain worth putting the queue through that on
   * the week of the event.
   */
  guests: {
    key: string;
    value: { id: string; code: string; sync: SyncState };
    indexes: { "by-sync": SyncState; "by-code": string };
  };
  uploads: {
    key: string;
    value: UploadRecord;
    indexes: { "by-sync": SyncState; "by-guest": string };
  };
  meta: { key: string; value: unknown };
}

const DB_NAME = "laqta";
const DB_VERSION = 2;

let dbPromise: Promise<IDBPDatabase<LaqtaDB>> | null = null;

export function db(): Promise<IDBPDatabase<LaqtaDB>> {
  if (!dbPromise) {
    dbPromise = openDB<LaqtaDB>(DB_NAME, DB_VERSION, {
      // Guarded by oldVersion. The v1 callback created the stores
      // unconditionally, which was fine while 1 was the only version — but on
      // any device that already holds a v1 database, re-running it throws
      // ConstraintError and the console cannot open its own queue.
      upgrade(database, oldVersion, _newVersion, tx) {
        if (oldVersion < 1) {
          const guests = database.createObjectStore("guests", { keyPath: "id" });
          guests.createIndex("by-sync", "sync");
          guests.createIndex("by-code", "code");

          const uploads = database.createObjectStore("uploads", { keyPath: "id" });
          uploads.createIndex("by-sync", "sync");
          uploads.createIndex("by-guest", "guestId");

          database.createObjectStore("meta");
        }

        if (oldVersion === 1) {
          // v1 could queue an upload with kind='selfie', which was sent to
          // upload-url's issue-selfie action. That action is gone and the
          // endpoint answers 410, so such a record can never succeed: it would
          // retry to its attempts cap and then sit in the queue bar as a
          // permanent red failure for the whole event.
          //
          // Dropped rather than re-labelled as 'external'. A selfie record was
          // deliberately stored WITHOUT a thumbnail, and re-labelling it would
          // create a media row whose thumb_path points at an object that does
          // not exist — a broken tile in the grid, which is worse than a photo
          // that was never a gallery photo in the first place.
          const uploads = tx.objectStore("uploads");
          void uploads.openCursor().then(async function drop(cursor) {
            if (!cursor) return;
            if (cursor.value.kind !== "backdrop") await cursor.delete();
            return drop(await cursor.continue());
          });
        }
      },
    });
  }
  return dbPromise;
}

/* --------------------------------- uploads --------------------------------- */

export async function putUpload(u: UploadRecord): Promise<void> {
  (await db()).put("uploads", u);
}

/**
 * Persists a whole batch in ONE transaction.
 *
 * This is the window in which a refresh loses photos, so it is kept as short as
 * physically possible: no compression, no network, one commit.
 */
export async function putUploadsBatch(records: UploadRecord[]): Promise<void> {
  const database = await db();
  const tx = database.transaction("uploads", "readwrite");
  for (const rec of records) tx.store.put(rec);
  await tx.done;
}

export async function getUpload(id: string): Promise<UploadRecord | undefined> {
  return (await db()).get("uploads", id);
}


export async function pendingUploads(): Promise<UploadRecord[]> {
  const database = await db();
  const pending = await database.getAllFromIndex("uploads", "by-sync", "pending");
  const failed = await database.getAllFromIndex("uploads", "by-sync", "failed");
  // Oldest first: the guest who has been waiting longest gets their photo first.
  return [...pending, ...failed].sort((a, b) =>
    a.createdAt.localeCompare(b.createdAt)
  );
}


export async function uploadCounts(): Promise<{
  pending: number;
  failed: number;
  synced: number;
}> {
  const all = await (await db()).getAll("uploads");
  return {
    pending: all.filter((u) => u.sync === "pending").length,
    failed: all.filter((u) => u.sync === "failed").length,
    synced: all.filter((u) => u.sync === "synced").length,
  };
}

/**
 * Drops the oldest confirmed uploads so the store cannot grow without bound
 * across a long event. Only `synced` records are eligible — anything still
 * pending or failed is queued work and is never pruned.
 */
export async function pruneSynced(keep = 100): Promise<number> {
  const database = await db();
  const synced = (await database.getAllFromIndex("uploads", "by-sync", "synced"))
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const excess = synced.slice(0, Math.max(0, synced.length - keep));
  for (const rec of excess) await database.delete("uploads", rec.id);
  return excess.length;
}

